import React, { FC } from 'react';
import Popup from 'reactjs-popup';
import styled from 'styled-components';
import { MediumText, MiniText } from '../../../../utils/typography';
import { commonPopupCSS, filterShadow, filterTableDropdownStyle, TooltipPosition } from '../../../../utils/popupUtils';

export type OptionItem = {
  key: any;
  value: string | React.ReactElement | ((close: () => void) => React.ReactElement);
  selected?: boolean;
  disabled?: boolean;
  onClick?: (key: string, close: Function) => void;
};

export type Section = { title?: string; options: OptionItem[] };

type Props = {
  disabled?: boolean;
  target: React.ReactElement | ((active: boolean, open: boolean) => React.ReactElement);
  sections: Section[];
  onClick?: (key: string, close: Function) => void;
  position?: TooltipPosition;
  className?: string;
  submenu?: boolean;
  width?: string;
};

const TableMenu: FC<Props> = ({ disabled, onClick, target, position, sections, className, width, submenu }) => {
  return (
    <Container className={className} submenu={submenu} disabled={disabled}>
      <Popup
        trigger={open => {
          const active = !!sections.find(s => s.options.find(o => o.selected));
          return <span>{target instanceof Function ? target(active, open) : target}</span>;
        }}
        disabled={disabled}
        position={position || 'bottom right'}
        on="click"
        contentStyle={{ ...filterTableDropdownStyle, width }}
        arrowStyle={{ display: 'none' }}
      >
        {close => (
          <>
            {sections.map((section, sectionIndex) => (
              <div key={sectionIndex}>
                {section.title && <SectionTitle key={sectionIndex}>{section.title}</SectionTitle>}
                {section.options.map((option, optionIndex) => (
                  <FilterOption
                    hasTitle={!!section.title}
                    key={option.key}
                    lastSection={sectionIndex === sections.length - 1}
                    lastOption={optionIndex === section.options.length - 1}
                    isString={typeof option.value === 'string'}
                    strong={option.selected}
                    onClick={() => {
                      if (option.onClick) {
                        option.onClick(option.key, close);
                      } else if (onClick) {
                        onClick(option.key, close);
                      }
                    }}
                  >
                    {option.value instanceof Function ? option.value(close) : option.value}
                  </FilterOption>
                ))}
              </div>
            ))}
          </>
        )}
      </Popup>
    </Container>
  );
};

export default TableMenu;

const SectionTitle = styled(MiniText)`
  cursor: auto;
  display: block;
  text-align: left;
  padding: 5px 10px;
`;

const Container = styled.div<{ submenu?: boolean; disabled?: boolean }>`
  display: flex;
  justify-content: flex-end;
  box-sizing: border-box;
  .submenu-content {
    background-color: ${({ theme }) => theme.background} !important;
  }
  ${props => props.disabled && `cursor: auto; background: ${props.theme.background}`};

  > .popup-content {
    ${commonPopupCSS}
    ${filterShadow}
    ${props => props.submenu && `box-shadow: 4px 3px 10px ${props.theme.boxShadow} !important;`}
    overflow: visible !important;
  }
`;

const FilterOption = styled(MediumText)<{ lastOption: boolean; lastSection: boolean; isString: boolean; hasTitle: boolean }>`
  > * {
    padding: ${props => `5px 10px 5px ${props.hasTitle ? 20 : 10}px;`};
  }
  ${props => props.isString && `padding: 5px 10px 5px ${props.hasTitle ? 25 : 10}px;`}};

  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  display: block;
  text-align: left;
  flex: 1;
  cursor: pointer;
  ${props => !props.lastSection && props.lastOption && `border-bottom: 1px solid ${props.theme.borderColor};`};
  &:hover {
    background: ${props => props.theme.backgroundLight};
  }
  > .submenu-content,
  ${Container} > .submenu-content {
    left: calc(100% + 1px) !important;
  }
`;
